import { Stack } from "@certn/cascadia/foundations/grid-layout/Stack";
import { css } from "@emotion/css";
import { useEffect, useState } from "react";
import { TextFieldProps } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { Chip } from "./Chip";
import { FieldErrorsProps } from "../_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";
import { TextField } from "../text-field/TextField";
import isNil from "lodash/isNil";

const EMAIL_REGEX = /(?:mailto:)?([^@\s"',<>;]+@[^@\s"',<>;]+\.[^@\s"',<>;]+)/g;
const EMAIL_IS_VALID_REGEX = /^[^@\s"',<>;]+@[^@\s"',<>;]+\.[^@\s"',<>;]+$/;

const isValidEmail = (email: string): boolean =>
  EMAIL_IS_VALID_REGEX.test(email);

export const extractEmailsFromText = (text: string): string[] => {
  const matches = text.match(EMAIL_REGEX);
  return matches ? matches.map((match) => match.replace(/^mailto:/, "")) : [];
};
interface UseEmailSelectorProps {
  initialEmails?: string[];
  isValidProp?: boolean;
  labelProp?: string;
  onChange?: (emails: string[]) => void;
  max?: number;
}

export const useEmailSelector = ({
  initialEmails = [],
  isValidProp = true,
  labelProp = "",
  onChange,
  max,
}: UseEmailSelectorProps) => {
  const [emails, setEmails] = useState<string[]>(initialEmails);
  const [inputValue, setInputValue] = useState("");
  const [isValid, setIsValid] = useState(isValidProp);
  const [label, setLabel] = useState(`${labelProp} (${emails.length})`);

  useEffect(() => {
    if (max !== 1) {
      setLabel(`${labelProp} (${emails.length})`);
    }
  }, [labelProp, emails, max]);

  const isEmailInList = (email: string) => emails.includes(email);

  const addEmail = (email: string) => {
    if (
      !isValidEmail(email) ||
      isEmailInList(email) ||
      (!isNil(max) && emails.length >= max)
    ) {
      return;
    }
    setEmails((prevEmails) => {
      const newEmails = [...prevEmails, email];
      setTimeout(() => {
        onChange?.(newEmails);
      });
      setLabel(`${labelProp} (${newEmails.length})`);
      return newEmails;
    });
    setInputValue("");
    setIsValid(true);
  };

  const removeEmail = (email: string) => {
    setEmails((prevEmails) => {
      const newEmails = prevEmails.filter((e) => e !== email);
      setTimeout(() => {
        onChange?.(newEmails);
      });
      setLabel(`${labelProp} (${newEmails.length})`);
      return newEmails;
    });
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setIsValid(
      value.length === 0 || (isValidEmail(value) && !isEmailInList(value))
    );
  };

  const handleBlur = () => {
    handlePaste(inputValue);
  };

  const handlePaste = (text: string) => {
    const newEmails = extractEmailsFromText(text);
    const uniqueNewEmails = newEmails.filter((email) => !isEmailInList(email));
    if (uniqueNewEmails.length > 0) {
      setEmails((prevEmails) => {
        const remaining = !isNil(max) ? max - prevEmails.length : Infinity;
        const toAdd = uniqueNewEmails.slice(0, remaining);
        if (toAdd.length === 0) return prevEmails;

        const updatedEmails = Array.from(new Set([...prevEmails, ...toAdd]));
        setInputValue("");
        setIsValid(true);
        setLabel(`${labelProp} (${updatedEmails.length})`);
        setTimeout(() => {
          onChange?.(updatedEmails);
        });
        return updatedEmails;
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handlePaste(inputValue);
    } else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };
  const handlePasteEvent = (evt: React.ClipboardEvent<HTMLInputElement>) => {
    evt.preventDefault();
    const paste = evt.clipboardData?.getData("text") || "";
    handlePaste(paste);
  };

  return {
    emails,
    inputValue,
    isValid,
    label,
    addEmail,
    removeEmail,
    handleInputChange,
    handlePaste,
    handleKeyDown,
    handleBlur,
    handlePasteEvent,
  };
};

export interface EmailSelectorProps
  extends Omit<TextFieldProps, "children" | "value" | "onChange"> {
  value?: string[];
  label?: string;
  testID: string;
  placeholder?: string;
  description?: string;
  errors?: FieldErrorsProps["children"];
  onChange?: (emails: string[]) => void;
  max?: number;
}

export function EmailSelector(props: EmailSelectorProps) {
  const {
    testID = "emails",
    label: labelProp = "Emails",
    description,
    onChange,
    value,
    errors,
    max,
  } = props;
  const { t } = useTranslation();

  const {
    emails,
    inputValue,
    isValid,
    label,
    removeEmail,
    handleInputChange,
    handleKeyDown,
    handleBlur,
    handlePasteEvent,
  } = useEmailSelector({
    initialEmails: value,
    isValidProp: !props.isInvalid,
    labelProp,
    onChange,
    max,
  });

  const isAtMax = !isNil(max) && emails.length >= max;

  return (
    <Stack gap="space-1" orientation="vertical">
      <Stack>
        <FormLabelRAC isRequired={props.isRequired}>{label}</FormLabelRAC>
      </Stack>
      {emails.length > 0 && (
        <Stack
          gap="space-2"
          orientation="horizontal"
          className={styles.chipContainer}
        >
          {emails.map((email) => (
            <Chip
              testID={`email-selector-chip-${email}`}
              key={email}
              onClick={() => removeEmail(email)}
              email={email}
            />
          ))}
        </Stack>
      )}
      <TextField
        aria-label={t("Enter email addresses to send this application to")}
        testID={`${testID}-email-selector-text-field`}
        isInvalid={!isValid}
        isDisabled={isAtMax}
        description={description}
        errors={errors}
        onPaste={handlePasteEvent}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        value={inputValue}
        onChange={handleInputChange}
        className={styles.flexOne}
      />
    </Stack>
  );
}

const styles = {
  flexOne: css({
    flex: 1,
  }),
  chipContainer: css({
    paddingBottom: "var(--space-2)",
    paddingTop: "var(--space-2)",
    flexWrap: "wrap",
    rowGap: "var(--space-2) !important",
  }),
};
